// Musikregeln in der Mediathek: gespeicherte Filter, die eine Stundenuhr für einen
// Musikplatz nutzen kann („Tanzbar: 118–132 BPM“, „80er“).
//
// Ehrlich statt schlau: Die Regeln fragen nur nach Messwerten und Eingetragenem.
// Wer kein gemessenes Tempo hat, passt zu keiner Tempo-Regel – das steht hier
// ausdrücklich dabei, statt dass sich jemand über eine kleine Zahl wundert.
//
// Eigene Datei, damit die lange Mediathek-Seite nicht noch länger wird.

import { useCallback, useEffect, useRef, useState } from "react";
import { musikregelApi, type MusikRegel, type MusikRegelVorschau } from "../api";
import {
  bedingungenZuFormular, formularZuBedingungen, FORMULAR_LEER, mmss, REGEL_FARBEN, REGEL_VORLAGEN, regelChips,
  type RegelFormular,
} from "../musikregeln-text";
import { CAMELOT_ALLE } from "./takt";
import { Icon, errorText } from "./streaming-ui";
import { Callout, EmptyState, Skeleton } from "./ui";
import { useAuth } from "../auth/AuthProvider";

/** Farbpunkt einer Regel – derselbe wie in der Stundenuhr. */
export function RegelPunkt({ farbe, className = "h-2.5 w-2.5" }: { farbe: string; className?: string }): JSX.Element {
  return <span className={`flex-shrink-0 rounded-full ${className}`} style={{ background: farbe }} aria-hidden="true" />;
}

/** Eine Bedingung als Chip. */
function BedingungChip({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <span className="inline-flex items-center rounded-full bg-[var(--bg-sunken)] px-2 py-0.5 text-[0.72rem] font-medium tabular-nums text-secondary ring-1 ring-[var(--border)]">
      {children}
    </span>
  );
}

function dauerText(ms: number): string {
  const min = Math.round(ms / 60_000);
  if (min < 60) return `${min} Min.`;
  const h = Math.floor(min / 60);
  return `${h} Std. ${min % 60} Min.`;
}

interface Bearbeitung { id: string | null; name: string; farbe: string; formular: RegelFormular }

export function MusikRegelnPanel(): JSX.Element {
  // Ändern darf wie bei den Stundenuhren nur die Radioleitung (der Server prüft
  // es ebenso) – alle anderen sehen die Regeln und was dazu passt.
  const { user } = useAuth();
  const leitung = user?.role === "tenant_admin" || user?.role === "super_admin";
  const [regeln, setRegeln] = useState<MusikRegel[] | null>(null);
  const [bearbeitung, setBearbeitung] = useState<Bearbeitung | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      setRegeln((await musikregelApi.liste()).regeln);
    } catch (err) {
      setError(errorText(err));
    }
  }, []);
  useEffect(() => void laden(), [laden]);

  const speichern = async (): Promise<void> => {
    if (!bearbeitung) return;
    setError(null);
    const input = { name: bearbeitung.name.trim(), farbe: bearbeitung.farbe, bedingungen: formularZuBedingungen(bearbeitung.formular) };
    try {
      if (bearbeitung.id) await musikregelApi.aendern(bearbeitung.id, input);
      else await musikregelApi.anlegen(input);
      setNotice(bearbeitung.id ? "Gespeichert. Uhren mit dieser Regel planen die kommenden Stunden neu." : "Regel angelegt. In einer Stundenuhr beim Musikplatz „nach Regel“ wählen.");
      setBearbeitung(null);
      await laden();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const loeschen = async (r: MusikRegel): Promise<void> => {
    const frage = r.in_uhren > 0
      ? `Regel „${r.name}“ löschen? Sie steckt in ${r.in_uhren} ${r.in_uhren === 1 ? "Stundenuhr" : "Stundenuhren"} – diese Plätze spielen danach aus der ganzen Musik.`
      : `Regel „${r.name}“ löschen?`;
    if (!window.confirm(frage)) return;
    try {
      await musikregelApi.loeschen(r.id);
      await laden();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const neu = (vorlage?: (typeof REGEL_VORLAGEN)[number]): void => {
    setNotice(null);
    // Eine Vorlage, die es als Regel schon gibt, bekommt einen freien Namen.
    const namen = new Set((regeln ?? []).map((r) => r.name.toLowerCase()));
    let name = vorlage?.name ?? "";
    for (let i = 2; name && namen.has(name.toLowerCase()); i++) name = `${vorlage!.name} ${i}`;
    setBearbeitung({
      id: null,
      name,
      farbe: vorlage?.farbe ?? REGEL_FARBEN[(regeln?.length ?? 0) % REGEL_FARBEN.length]!,
      formular: vorlage ? bedingungenZuFormular(vorlage.bedingungen) : FORMULAR_LEER,
    });
  };

  if (bearbeitung) {
    return (
      <div className="space-y-4">
        {error && <Callout tone="danger">{error}</Callout>}
        <RegelEditor wert={bearbeitung} onChange={setBearbeitung} onSave={speichern} onCancel={() => { setBearbeitung(null); setError(null); }} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="max-w-3xl text-sm text-secondary">
        Eine Musikregel sortiert die Musik nach dem, was <strong className="font-semibold text-primary">gemessen oder eingetragen</strong> ist:
        Tempo und Tonart aus der Taktanalyse, Jahr, Dauer, Tags, Interpret. Stundenuhren können einen Musikplatz „nach Regel“ füllen.
        Geraten wird nichts – ein Titel ohne gemessenes Tempo passt zu keiner Tempo-Regel.
      </p>
      {error && <Callout tone="danger">{error}</Callout>}
      {notice && <Callout tone="success">{notice}</Callout>}

      {leitung && <div className="flex flex-wrap items-center gap-1.5">
        <button onClick={() => neu()} className="btn btn-primary btn-sm mr-1"><Icon name="plus" className="h-3.5 w-3.5" /> Neue Regel</button>
        <span className="mr-1 text-xs text-muted">oder Vorlage:</span>
        {REGEL_VORLAGEN.map((v) => (
          <button key={v.name} onClick={() => neu(v)} title={v.hinweis}
            className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-[var(--border-strong)] px-2.5 py-1 text-xs font-medium text-secondary hover:border-solid hover:bg-[var(--bg-hover)]">
            <RegelPunkt farbe={v.farbe} className="h-2 w-2" />
            {v.name}: {regelChips(v.bedingungen).join(", ")}
          </button>
        ))}
      </div>}

      {regeln === null ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => <div key={i} className="card space-y-2 p-5"><Skeleton className="h-4 w-1/3" /><Skeleton className="h-5 w-2/3" /></div>)}
        </div>
      ) : regeln.length === 0 ? (
        <EmptyState
          icon={<Icon name="music" />}
          title="Noch keine Musikregel."
          description="Oben eine Vorlage anklicken – etwa „Tanzbar“ – und in der Vorschau sehen, welche Titel dazugehören."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {regeln.map((r) => {
            const chips = regelChips(r.bedingungen);
            return (
              <section key={r.id} className="card flex flex-col">
                <div className="min-w-0 space-y-2.5 p-5">
                  <div className="flex items-center gap-2">
                    <RegelPunkt farbe={r.farbe} />
                    <h3 className="truncate text-[0.95rem] font-semibold text-primary">{r.name}</h3>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {chips.length > 0 ? chips.map((c) => <BedingungChip key={c}>{c}</BedingungChip>) : <BedingungChip>alle Musik</BedingungChip>}
                  </div>
                  <div className="text-xs text-muted">
                    <span className={r.anzahl === 0 ? "font-medium text-amber-700 dark:text-amber-300" : ""}>
                      {r.anzahl === 0 ? "Im Moment passt kein Titel" : `${r.anzahl.toLocaleString("de-DE")} ${r.anzahl === 1 ? "Titel passt" : "Titel passen"}`}
                    </span>
                    {r.in_uhren > 0 ? ` · in ${r.in_uhren} ${r.in_uhren === 1 ? "Stundenuhr" : "Stundenuhren"}` : " · in keiner Stundenuhr"}
                  </div>
                </div>
                {leitung && <div className="mt-auto flex flex-wrap gap-2 border-t border-default px-5 py-3">
                  <button onClick={() => { setNotice(null); setBearbeitung({ id: r.id, name: r.name, farbe: r.farbe, formular: bedingungenZuFormular(r.bedingungen) }); }}
                    className="btn btn-secondary btn-sm">Bearbeiten</button>
                  <button onClick={() => void loeschen(r)} className="btn btn-ghost btn-sm ml-auto text-red-600 dark:text-red-400">
                    <Icon name="trash" className="h-3.5 w-3.5" /> Löschen
                  </button>
                </div>}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Ein Feld mit Beschriftung. */
function Feld({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }): JSX.Element {
  return (
    <label className="block min-w-0">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint block">{hint}</span>}
    </label>
  );
}

function RegelEditor({ wert, onChange, onSave, onCancel }: {
  wert: Bearbeitung;
  onChange: (w: Bearbeitung) => void;
  onSave: () => Promise<void>;
  onCancel: () => void;
}): JSX.Element {
  const f = wert.formular;
  const set = (patch: Partial<RegelFormular>): void => onChange({ ...wert, formular: { ...f, ...patch } });
  const bedingungen = formularZuBedingungen(f);
  const schluessel = JSON.stringify(bedingungen);
  const tempo = bedingungen.bpm_min !== undefined || bedingungen.bpm_max !== undefined;

  // Live-Vorschau, entprellt: erst fragen, wenn eine Weile nicht getippt wurde.
  const [vorschau, setVorschau] = useState<MusikRegelVorschau | null>(null);
  const [vorschauFehler, setVorschauFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);
  const zaehler = useRef(0);
  useEffect(() => {
    const nr = ++zaehler.current;
    setLaedt(true);
    const t = setTimeout(() => {
      musikregelApi.vorschau(JSON.parse(schluessel))
        .then((v) => { if (nr === zaehler.current) { setVorschau(v); setVorschauFehler(null); } })
        .catch((err) => { if (nr === zaehler.current) setVorschauFehler(errorText(err)); })
        .finally(() => { if (nr === zaehler.current) setLaedt(false); });
    }, 350);
    return () => clearTimeout(t);
  }, [schluessel]);

  const jahrzehnt = (j: number): void => set({ jahr_von: String(j), jahr_bis: String(j + 9) });
  const tonart = (k: string): void => set({ tonarten: f.tonarten.includes(k) ? f.tonarten.filter((x) => x !== k) : [...f.tonarten, k] });

  return (
    <div className="card flex flex-col gap-6 p-4 sm:p-5 lg:flex-row">
      <div className="min-w-0 flex-1 space-y-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
          <Feld label="Name">
            <input className="input" value={wert.name} onChange={(e) => onChange({ ...wert, name: e.target.value })} placeholder="z. B. Tanzbar" maxLength={60} />
          </Feld>
          <div>
            <span className="field-label">Farbe</span>
            <div className="flex min-h-[2.375rem] flex-wrap items-center">
              {REGEL_FARBEN.map((c) => (
                <button key={c} type="button" onClick={() => onChange({ ...wert, farbe: c })} aria-label={`Farbe ${c}`} aria-pressed={wert.farbe === c}
                  className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-[var(--bg-hover)]">
                  <span className={`h-6 w-6 rounded-full ring-offset-2 ring-offset-[var(--bg-card)] ${wert.farbe === c ? "ring-2 ring-[var(--text-primary)]" : ""}`} style={{ background: c }} />
                </button>
              ))}
            </div>
          </div>
        </div>

        <fieldset className="space-y-2">
          <legend className="section-label mb-2">Tempo</legend>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Feld label="von BPM"><input className="input tabular-nums" inputMode="decimal" value={f.bpm_min} onChange={(e) => set({ bpm_min: e.target.value })} placeholder="egal" /></Feld>
            <Feld label="bis BPM"><input className="input tabular-nums" inputMode="decimal" value={f.bpm_max} onChange={(e) => set({ bpm_max: e.target.value })} placeholder="egal" /></Feld>
            <Feld label="Messung">
              <select className="input" value={f.sicherheit} onChange={(e) => set({ sicherheit: e.target.value })} disabled={!tempo}>
                <option value="">jede Messung</option>
                <option value="0.6">mind. ziemlich sicher</option>
                <option value="0.8">nur sichere</option>
              </select>
            </Feld>
          </div>
          <p className="field-hint">
            Grenzen zählen mit: „118–132“ nimmt 118 und 132. Titel <strong className="font-medium">ohne gemessenes Tempo passen nicht</strong> – die Regel rät nicht.
            Von Hand gesetzte Werte gelten immer als sicher.
          </p>
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="section-label mb-2">Jahr</legend>
          <div className="grid grid-cols-2 gap-3">
            <Feld label="von"><input className="input tabular-nums" inputMode="numeric" value={f.jahr_von} onChange={(e) => set({ jahr_von: e.target.value })} placeholder="egal" /></Feld>
            <Feld label="bis"><input className="input tabular-nums" inputMode="numeric" value={f.jahr_bis} onChange={(e) => set({ jahr_bis: e.target.value })} placeholder="egal" /></Feld>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-muted">Jahrzehnt:</span>
            {[1960, 1970, 1980, 1990, 2000, 2010, 2020].map((j) => (
              <button key={j} type="button" onClick={() => jahrzehnt(j)}
                className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-[var(--border)] ${f.jahr_von === String(j) && f.jahr_bis === String(j + 9) ? "bg-brand-primary/15 text-brand-primary" : "text-secondary hover:bg-[var(--bg-hover)]"}`}>
                {String(j).slice(2)}er
              </button>
            ))}
          </div>
          <p className="field-hint">Das Jahr des Titels; fehlt es, das Jahr der Veröffentlichung. Titel ganz ohne Jahr passen nicht.</p>
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="section-label mb-2">Tags</legend>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Feld label="einer davon"><input className="input" value={f.tags_irgendeiner} onChange={(e) => set({ tags_irgendeiner: e.target.value })} placeholder="z. B. sommer, strand" /></Feld>
            <Feld label="alle davon"><input className="input" value={f.tags_alle} onChange={(e) => set({ tags_alle: e.target.value })} placeholder="z. B. deutsch, neu" /></Feld>
            <Feld label="keiner davon"><input className="input" value={f.tags_keiner} onChange={(e) => set({ tags_keiner: e.target.value })} placeholder="z. B. weihnachten" /></Feld>
          </div>
          <p className="field-hint">Mehrere mit Komma trennen.</p>
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="section-label mb-2">Dauer und Interpret</legend>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Feld label="kürzer als (m:ss)"><input className="input tabular-nums" value={f.dauer_max} onChange={(e) => set({ dauer_max: e.target.value })} placeholder="egal, z. B. 3:30" /></Feld>
            <Feld label="Interpret enthält"><input className="input" value={f.interpret_enthaelt} onChange={(e) => set({ interpret_enthaelt: e.target.value })} placeholder="egal" /></Feld>
            <Feld label="Interpret enthält nicht"><input className="input" value={f.interpret_nicht} onChange={(e) => set({ interpret_nicht: e.target.value })} placeholder="egal" /></Feld>
          </div>
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="section-label mb-2">Tonart</legend>
          <div className="grid grid-cols-6 gap-1 sm:grid-cols-12">
            {CAMELOT_ALLE.map((k) => (
              <button key={k} type="button" onClick={() => tonart(k)} aria-pressed={f.tonarten.includes(k)}
                className={`h-7 rounded-md text-xs font-medium tabular-nums ring-1 ring-[var(--border)] ${f.tonarten.includes(k) ? "bg-brand-primary text-white ring-transparent" : "text-secondary hover:bg-[var(--bg-hover)]"}`}>
                {k}
              </button>
            ))}
          </div>
          <p className="field-hint">Camelot-Schreibweise wie in der Mediathek. Keine gewählt heißt: Tonart egal.</p>
        </fieldset>

        <div className="flex gap-2 border-t border-default pt-4">
          <button onClick={() => void onSave()} disabled={!wert.name.trim()} className="btn btn-primary">Speichern</button>
          <button onClick={onCancel} className="btn btn-ghost">Abbrechen</button>
        </div>
      </div>

      {/* Auf dem Telefon steht die Vorschau unter dem langen Formular – die Zahl bleibt darum unten sichtbar. */}
      <div className="sticky bottom-3 z-10 -mt-2 lg:hidden" aria-hidden="true">
        <div className="flex items-center gap-2 rounded-full bg-card px-4 py-2 text-sm shadow-lg ring-1 ring-[var(--border)]">
          <RegelPunkt farbe={wert.farbe} />
          {vorschau ? (
            <span className="text-secondary"><strong className="tabular-nums text-primary">{vorschau.anzahl.toLocaleString("de-DE")}</strong> von {vorschau.gesamt.toLocaleString("de-DE")} Titeln passen</span>
          ) : <span className="text-muted">Vorschau …</span>}
          {laedt && <span className="ml-auto text-xs text-muted">rechnet …</span>}
        </div>
      </div>

      <aside className="min-w-0 lg:w-80 lg:border-l lg:border-default lg:pl-6" aria-live="polite">
        <div className="lg:sticky lg:top-4 space-y-3">
          <div className="flex items-center gap-2">
            <RegelPunkt farbe={wert.farbe} />
            <span className="section-label">Vorschau</span>
            {laedt && <span className="ml-auto text-xs text-muted">rechnet …</span>}
          </div>
          {vorschauFehler ? (
            <Callout tone="danger">{vorschauFehler}</Callout>
          ) : !vorschau ? (
            <div className="space-y-2"><Skeleton className="h-8 w-1/2" /><Skeleton className="h-3 w-full" /><Skeleton className="h-3 w-4/5" /></div>
          ) : (
            <>
              <div>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-2xl font-semibold tabular-nums text-primary">{vorschau.anzahl.toLocaleString("de-DE")}</span>
                  <span className="text-sm text-secondary">von {vorschau.gesamt.toLocaleString("de-DE")} Musiktiteln</span>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[var(--bg-sunken)]">
                  <div className="h-full rounded-full" style={{ width: `${vorschau.gesamt ? Math.max(vorschau.anzahl > 0 ? 2 : 0, (vorschau.anzahl / vorschau.gesamt) * 100) : 0}%`, background: wert.farbe }} />
                </div>
                {vorschau.anzahl > 0 && <p className="mt-1.5 text-xs text-muted">zusammen {dauerText(vorschau.dauer_ms)} Musik</p>}
              </div>

              {vorschau.anzahl === 0 && (
                <Callout tone="warning">
                  Im Moment passt kein Titel. Ein Uhr-Platz mit dieser Regel spielt dann aus der ganzen Musik – und vermerkt das im Sendeablauf.
                </Callout>
              )}
              <FehlendeWerte ohne={vorschau.ohne_wert} />

              {vorschau.beispiele.length > 0 && (
                <div>
                  <div className="mb-1 text-xs font-medium text-secondary">Zum Beispiel</div>
                  <ul className="divide-y divide-[var(--border)] rounded-lg ring-1 ring-[var(--border)]">
                    {vorschau.beispiele.map((b) => (
                      <li key={b.id} className="flex min-w-0 items-baseline gap-2 px-2.5 py-1.5 text-xs">
                        <span className="min-w-0 flex-1 truncate">
                          <span className="font-medium text-primary">{b.title}</span>
                          {b.artist && <span className="text-muted"> · {b.artist}</span>}
                        </span>
                        <span className="flex-shrink-0 tabular-nums text-muted">
                          {[
                            b.bpm !== null ? `${Math.round(b.bpm)} BPM${b.bpm_source === "manual" ? " (Hand)" : ""}` : null,
                            b.jahr,
                            b.tonart,
                            b.duration_ms ? mmss(Math.round(b.duration_ms / 1000)) : null,
                          ].filter(Boolean).join(" · ")}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

/** Wie viele Titel den gefragten Wert gar nicht haben – und deshalb nie passen. */
function FehlendeWerte({ ohne }: { ohne: MusikRegelVorschau["ohne_wert"] }): JSX.Element | null {
  const zeilen: string[] = [];
  const t = (n: number | undefined, was: string): void => {
    if (n && n > 0) zeilen.push(`${n.toLocaleString("de-DE")} ${n === 1 ? "Titel hat" : "Titel haben"} ${was}`);
  };
  t(ohne.tempo, "kein gemessenes Tempo");
  t(ohne.jahr, "kein Jahr");
  t(ohne.tonart, "keine gemessene Tonart");
  t(ohne.dauer, "keine bekannte Dauer");
  if (zeilen.length === 0) return null;
  return (
    <p className="rounded-lg bg-[var(--bg-sunken)] px-3 py-2 text-xs text-secondary ring-1 ring-[var(--border)]">
      {zeilen.join(", ")}. Solche Titel passen nie zu dieser Regel.
      {ohne.tempo ? " Die Taktanalyse misst nach; ein Tempo lässt sich auch in der Titelzeile von Hand setzen." : ""}
    </p>
  );
}
